import * as React from "react"
import { ArrowRight, Loader2, ShieldQuestion } from "lucide-react"

import { BookColumn } from "@/components/BookMark"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { SelectorDeFecha } from "@/components/SelectorDeFecha"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  MANAGEMENT_ACTION_LABELS,
  STATUTORY_ACTION_LABELS,
  TAX_ACTION_LABELS,
  type ManagementAction,
  type StatutoryAction,
  type TaxAction,
  type TreatmentRule,
  type TreatmentScopeType,
} from "@/lib/types"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

const MIN_REASON_LENGTH = 10

const SCOPE_LABELS: Record<TreatmentScopeType, string> = {
  cuenta: "Cuenta contable",
  categoria: "Categoria de gasto",
  proveedor: "Proveedor (RUC)",
  etiqueta: "Etiqueta",
  condicion: "Condicion",
}

const SCOPE_PLACEHOLDERS: Record<TreatmentScopeType, string> = {
  cuenta: "65911",
  categoria: "servicios_profesionales",
  proveedor: "20601234567",
  etiqueta: "bono_extraordinario",
  condicion: "documento.total > 10000",
}

interface Draft {
  name: string
  scopeType: TreatmentScopeType
  scopeValue: string
  statutoryAction: StatutoryAction
  statutoryLimitPct: string
  statutoryNote: string
  taxAction: TaxAction
  taxLimitPct: string
  taxLegalBasis: string
  taxNote: string
  managementAction: ManagementAction
  managementTargetAccount: string
  managementNormalizedValue: string
  managementExcludeFromEbitda: boolean
  managementNote: string
  effectiveFrom: string
  effectiveTo: string
}

const EMPTY_DRAFT: Draft = {
  name: "",
  scopeType: "cuenta",
  scopeValue: "",
  statutoryAction: "incluir",
  statutoryLimitPct: "",
  statutoryNote: "",
  taxAction: "incluir",
  taxLimitPct: "",
  taxLegalBasis: "",
  taxNote: "",
  managementAction: "incluir",
  managementTargetAccount: "",
  managementNormalizedValue: "",
  managementExcludeFromEbitda: false,
  managementNote: "",
  effectiveFrom: "",
  effectiveTo: "",
}

function toDraft(rule: TreatmentRule): Draft {
  return {
    name: rule.name,
    scopeType: rule.scopeType,
    scopeValue: rule.scopeValue,
    statutoryAction: rule.statutory.action,
    statutoryLimitPct: rule.statutory.limitPct ? String(rule.statutory.limitPct) : "",
    statutoryNote: rule.statutory.note ?? "",
    taxAction: rule.tax.action,
    taxLimitPct: rule.tax.limitPct ? String(rule.tax.limitPct) : "",
    taxLegalBasis: rule.tax.legalBasis ?? "",
    taxNote: rule.tax.note ?? "",
    managementAction: rule.management.action,
    managementTargetAccount: rule.management.targetAccount ?? "",
    managementNormalizedValue: rule.management.normalizedValue
      ? String(rule.management.normalizedValue)
      : "",
    managementExcludeFromEbitda: Boolean(rule.management.excludeFromEbitda),
    managementNote: rule.management.note ?? "",
    effectiveFrom: rule.effectiveFrom,
    effectiveTo: rule.effectiveTo ?? "",
  }
}

interface TreatmentRuleEditorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rule: TreatmentRule | null
  pending?: boolean
  onSubmit: (rule: Partial<TreatmentRule>, reason: string) => void | Promise<void>
}

/**
 * Editor de treatment_rule.
 *
 * Aqui una persona no tecnica decide si algo entra al P&L, solo a contabilidad
 * o solo a lo tributario. Las tres columnas de libro se muestran SIEMPRE
 * juntas y con el mismo peso visual: la decision es la relacion entre ellas,
 * no cada una por separado. Debajo, una frase en lenguaje llano resume el
 * efecto combinado — que es lo unico que quien firma va a leer de verdad.
 */
export function TreatmentRuleEditor({
  open,
  onOpenChange,
  rule,
  pending,
  onSubmit,
}: TreatmentRuleEditorProps) {
  const [draft, setDraft] = React.useState<Draft>(EMPTY_DRAFT)
  const [reason, setReason] = React.useState("")
  const [touched, setTouched] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setDraft(rule ? toDraft(rule) : { ...EMPTY_DRAFT, effectiveFrom: "2026-09-01" })
    setReason("")
    setTouched(false)
  }, [open, rule])

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }) as Draft)
  }

  const reasonInvalid = reason.trim().length < MIN_REASON_LENGTH
  const nameInvalid = draft.name.trim().length < 3
  const scopeInvalid = draft.scopeValue.trim().length === 0
  const fromInvalid = draft.effectiveFrom.length === 0
  const invalid = reasonInvalid || nameInvalid || scopeInvalid || fromInvalid

  /** Divergencia entre libros: es la senal de que la regla hace algo interesante. */
  const diverges =
    draft.statutoryAction !== "incluir" ||
    draft.taxAction !== "incluir" ||
    draft.managementAction !== "incluir" ||
    draft.managementExcludeFromEbitda

  async function handleSubmit() {
    setTouched(true)
    if (invalid) return
    await onSubmit(
      {
        id: rule?.id,
        name: draft.name.trim(),
        scopeType: draft.scopeType,
        scopeValue: draft.scopeValue.trim(),
        statutory: {
          action: draft.statutoryAction,
          limitPct: draft.statutoryLimitPct ? Number(draft.statutoryLimitPct) : undefined,
          note: draft.statutoryNote || undefined,
        },
        tax: {
          action: draft.taxAction,
          limitPct: draft.taxLimitPct ? Number(draft.taxLimitPct) : undefined,
          legalBasis: draft.taxLegalBasis || undefined,
          note: draft.taxNote || undefined,
        },
        management: {
          action: draft.managementAction,
          targetAccount: draft.managementTargetAccount || undefined,
          normalizedValue: draft.managementNormalizedValue
            ? Number(draft.managementNormalizedValue)
            : undefined,
          excludeFromEbitda: draft.managementExcludeFromEbitda,
          note: draft.managementNote || undefined,
        },
        effectiveFrom: draft.effectiveFrom,
        effectiveTo: draft.effectiveTo || undefined,
      },
      reason.trim(),
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>
            {rule ? (
              <span className="flex flex-wrap items-baseline gap-2">
                {t("Editar regla")}<span className="code text-xs font-normal text-muted-foreground">{rule.id}</span>
                <span className="font-normal text-muted-foreground">·</span>
                <span>{rule.name}</span>
              </span>
            ) : (
              "Nueva regla de tratamiento"
            )}
          </DialogTitle>
          <DialogDescription>
            {t("Un mismo hecho economico se asienta en varios libros. Aqui se define que hace cada uno.")}</DialogDescription>
        </DialogHeader>

        {/* ---- Alcance: a que se aplica ---- */}
        <fieldset className="grid gap-3 sm:grid-cols-4">
          <legend className="eyebrow mb-1.5">{t("Alcance")}</legend>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="regla-nombre">{t("Nombre de la regla *")}</Label>
            <Input
              id="regla-nombre"
              value={draft.name}
              onChange={(event) => set("name", event.target.value)}
              placeholder={t("Multas y sanciones")}
              aria-invalid={touched && nameInvalid}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="regla-alcance">{t("Aplica a *")}</Label>
            <Select
              value={draft.scopeType}
              onValueChange={(value) => set("scopeType", value as TreatmentScopeType)}
            >
              <SelectTrigger id="regla-alcance">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(SCOPE_LABELS) as TreatmentScopeType[]).map((scope) => (
                  <SelectItem key={scope} value={scope}>
                    {SCOPE_LABELS[scope]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="regla-valor">{t("Valor *")}</Label>
            <Input
              id="regla-valor"
              value={draft.scopeValue}
              onChange={(event) => set("scopeValue", event.target.value)}
              placeholder={SCOPE_PLACEHOLDERS[draft.scopeType]}
              className="font-mono"
              aria-invalid={touched && scopeInvalid}
            />
          </div>
        </fieldset>

        {/* ---- Las tres columnas de libro, siempre juntas y del mismo peso ---- */}
        <div>
          <h3 className="eyebrow mb-1.5">{t("Tratamiento por libro")}</h3>
          <div className="grid gap-3 lg:grid-cols-3">
            <BookColumn kind="statutory">
              <Field label={t("Accion")} htmlFor="st-action">
                <Select
                  value={draft.statutoryAction}
                  onValueChange={(value) => set("statutoryAction", value as StatutoryAction)}
                >
                  <SelectTrigger id="st-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(STATUTORY_ACTION_LABELS) as StatutoryAction[]).map((action) => (
                      <SelectItem key={action} value={action}>
                        {STATUTORY_ACTION_LABELS[action]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {draft.statutoryAction === "importe_con_limite" ? (
                <Field label={t("Limite (0-1)")} htmlFor="st-limit">
                  <Input
                    id="st-limit"
                    value={draft.statutoryLimitPct}
                    onChange={(event) => set("statutoryLimitPct", event.target.value)}
                    className="text-right font-mono"
                    inputMode="decimal"
                    placeholder="0.005"
                  />
                </Field>
              ) : null}

              <Field label={t("Nota")} htmlFor="st-note">
                <Textarea
                  id="st-note"
                  value={draft.statutoryNote}
                  onChange={(event) => set("statutoryNote", event.target.value)}
                  className="min-h-[3.25rem] text-xs"
                  placeholder={t("Referencia a la norma contable aplicada.")}
                />
              </Field>
            </BookColumn>

            <BookColumn kind="tax">
              <Field label={t("Accion")} htmlFor="tx-action">
                <Select
                  value={draft.taxAction}
                  onValueChange={(value) => set("taxAction", value as TaxAction)}
                >
                  <SelectTrigger id="tx-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(TAX_ACTION_LABELS) as TaxAction[]).map((action) => (
                      <SelectItem key={action} value={action}>
                        {TAX_ACTION_LABELS[action]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {draft.taxAction === "limite" ? (
                <Field label={t("Limite (0-1)")} htmlFor="tx-limit">
                  <Input
                    id="tx-limit"
                    value={draft.taxLimitPct}
                    onChange={(event) => set("taxLimitPct", event.target.value)}
                    className="text-right font-mono"
                    inputMode="decimal"
                    placeholder="0.005"
                  />
                </Field>
              ) : null}

              <Field
                label={t("Base normativa")}
                htmlFor="tx-basis"
                hint="Se cita tal cual en el papel de trabajo de la determinacion."
              >
                <Input
                  id="tx-basis"
                  value={draft.taxLegalBasis}
                  onChange={(event) => set("taxLegalBasis", event.target.value)}
                  placeholder={t("Art. 44 inc. c LIR")}
                />
              </Field>

              <Field label={t("Nota")} htmlFor="tx-note">
                <Textarea
                  id="tx-note"
                  value={draft.taxNote}
                  onChange={(event) => set("taxNote", event.target.value)}
                  className="min-h-[3.25rem] text-xs"
                />
              </Field>
            </BookColumn>

            <BookColumn kind="management">
              <Field label={t("Accion")} htmlFor="mg-action">
                <Select
                  value={draft.managementAction}
                  onValueChange={(value) => set("managementAction", value as ManagementAction)}
                >
                  <SelectTrigger id="mg-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(MANAGEMENT_ACTION_LABELS) as ManagementAction[]).map((action) => (
                      <SelectItem key={action} value={action}>
                        {MANAGEMENT_ACTION_LABELS[action]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {draft.managementAction === "reclasificar" ? (
                <Field label={t("Cuenta destino")} htmlFor="mg-account">
                  <Input
                    id="mg-account"
                    value={draft.managementTargetAccount}
                    onChange={(event) => set("managementTargetAccount", event.target.value)}
                    className="font-mono"
                    placeholder="65911"
                  />
                </Field>
              ) : null}

              {draft.managementAction === "normalizar" ? (
                <Field
                  label={t("Valor normalizado")}
                  htmlFor="mg-value"
                  hint="Reemplaza el importe real solo en el libro gerencial."
                >
                  <Input
                    id="mg-value"
                    value={draft.managementNormalizedValue}
                    onChange={(event) => set("managementNormalizedValue", event.target.value)}
                    className="text-right font-mono"
                    inputMode="decimal"
                    placeholder="14000"
                  />
                </Field>
              ) : null}

              <label className="flex cursor-pointer items-start gap-2 rounded-md border bg-surface-sunken/60 p-2 text-xs">
                <Checkbox
                  className="mt-px"
                  checked={draft.managementExcludeFromEbitda}
                  onCheckedChange={(checked) => set("managementExcludeFromEbitda", Boolean(checked))}
                />
                <span>
                  <span className="font-medium">{t("Excluir del EBITDA ajustado")}</span>
                  <span className="mt-0.5 block text-2xs text-muted-foreground">
                    {t("El gasto sigue en el P&L, pero no castiga la metrica con la que se compara mes a mes.")}</span>
                </span>
              </label>

              <Field label={t("Nota")} htmlFor="mg-note">
                <Textarea
                  id="mg-note"
                  value={draft.managementNote}
                  onChange={(event) => set("managementNote", event.target.value)}
                  className="min-h-[3.25rem] text-xs"
                />
              </Field>
            </BookColumn>
          </div>
        </div>

        {/* ---- Resumen en lenguaje llano ---- */}
        <div
          className={cn(
            "rounded-md border p-2.5",
            diverges ? "border-brand/30 bg-brand-soft" : "border-border bg-surface-sunken",
          )}
        >
          <div className="eyebrow mb-1 flex items-center gap-1.5">
            <ArrowRight className="h-3 w-3" />
            {t("Como queda")}</div>
          <p className="text-xs leading-[1.2rem]">
            {t("En")}{" "}<strong className="text-book-statutory">statutory</strong> se{" "}
            <strong>{STATUTORY_ACTION_LABELS[draft.statutoryAction].toLowerCase()}</strong>{t("; en")}{" "}
            <strong className="text-book-tax">tributario</strong> se{" "}
            <strong>{TAX_ACTION_LABELS[draft.taxAction].toLowerCase()}</strong>{t("; en")}{" "}
            <strong className="text-book-management">gerencial</strong> se{" "}
            <strong>{MANAGEMENT_ACTION_LABELS[draft.managementAction].toLowerCase()}</strong>
            {draft.managementExcludeFromEbitda ? (
              <> {" "}{t("y queda")}{" "}<strong>{t("fuera del EBITDA ajustado")}</strong></>
            ) : null}
            .
          </p>
          {diverges ? (
            <p className="mt-1 flex items-start gap-1.5 text-2xs text-muted-foreground">
              <ShieldQuestion className="mt-px h-3 w-3 shrink-0" />
              {t("Los libros divergen: el mismo importe se vera distinto segun donde se mire. Es deliberado, y por eso el motivo es obligatorio.")}</p>
          ) : null}
        </div>

        {/* ---- Vigencia ---- */}
        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="eyebrow mb-1.5">{t("Vigencia")}</legend>
          <div className="space-y-1">
            <Label htmlFor="vig-desde">{t("Vigente desde *")}</Label>
            <SelectorDeFecha
              id="vig-desde"
              value={draft.effectiveFrom}
              onChange={(v) => set("effectiveFrom", v)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="vig-hasta">{t("Vigente hasta")}</Label>
            <SelectorDeFecha
              id="vig-hasta"
              value={draft.effectiveTo}
              onChange={(v) => set("effectiveTo", v)}
            />
            <p className="text-2xs text-muted-foreground">{t("Vacio = sin fecha de termino.")}</p>
          </div>
        </fieldset>

        {/* ---- Motivo ---- */}
        <div className="space-y-1">
          <Label htmlFor="regla-motivo">
            {t("Motivo")}{" "}<span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="regla-motivo"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={t("Por que se define asi el tratamiento en cada libro.")}
            className="min-h-[4rem]"
            aria-invalid={touched && reasonInvalid}
          />
          <p
            className={cn(
              "text-2xs text-muted-foreground",
              touched && reasonInvalid && "font-medium text-destructive",
            )}
          >
            {t("Obligatorio, minimo")}{" "}{MIN_REASON_LENGTH}{" "}{t("caracteres. Cada cambio crea una regla nueva con vigencia: los periodos ya cerrados conservan la regla que estaba vigente cuando se cerraron.")}</p>
        </div>

        <DialogFooter>
          {touched && invalid ? (
            <p className="mr-auto text-2xs font-medium text-destructive">
              {t("Falta completar nombre, valor de alcance, fecha de vigencia y motivo.")}</p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={pending}>
            {t("Cancelar")}</Button>
          <Button size="sm" onClick={handleSubmit} disabled={invalid || pending}>
            {pending ? <Loader2 className="animate-spin" /> : null}
            {t("Guardar regla con vigencia")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string
  htmlFor: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint ? <p className="text-2xs leading-4 text-muted-foreground">{hint}</p> : null}
    </div>
  )
}
